import {
  getScrollContainer,
  getScrollEventTarget,
  getScrollTop,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const mobileQuery = window.matchMedia('(max-width: 650px)');
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const mobileToolbarQuery = window.matchMedia('(max-width: 989px)');

class SterlingCollectionStickyToolbar extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.results = this.closest('results-list');
    this.header = document.querySelector('#header-component[data-sterling-navigation]');
    this.nav = this.header?.querySelector('.sterling-nav');
    this.frame = null;
    this.updateFrame = null;

    this.scheduleUpdate = () => {
      if (this.updateFrame !== null) return;
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    };

    this.bindScrollTarget = () => {
      const target = getScrollEventTarget();
      if (target === this.scrollTarget) return;
      this.scrollTarget?.removeEventListener('scroll', this.scheduleUpdate);
      this.scrollTarget = target;
      this.scrollTarget.addEventListener('scroll', this.scheduleUpdate, { passive: true });
    };

    window.addEventListener('resize', this.scheduleUpdate, { signal });
    window.visualViewport?.addEventListener('resize', this.scheduleUpdate, { signal });
    mobileToolbarQuery.addEventListener('change', this.scheduleUpdate, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.scheduleUpdate, { signal });

    this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
    if (this.nav) this.resizeObserver.observe(this.nav);
    this.resizeObserver.observe(this.results);

    this.bindScrollTarget();
    this.update();
  }

  getToolbar() {
    const selector = mobileToolbarQuery.matches ? '.facets-toggle' : '.facets-block-wrapper--horizontal';
    return this.results?.querySelector(selector);
  }

  update() {
    if (!this.results) return;
    this.bindScrollTarget();
    const toolbar = this.getToolbar();
    if (!toolbar) return;

    const scrollContainer = getScrollContainer();
    const rootTop = scrollContainer === document.scrollingElement ? 0 : scrollContainer.getBoundingClientRect().top;
    const fallback = Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 72;
    const navBottom = this.nav?.getBoundingClientRect().bottom;
    const toolbarTop = Math.round((Number.isFinite(navBottom) ? navBottom - rootTop : fallback) + 10);

    this.results.style.setProperty('--sterling-collection-toolbar-top', `${toolbarTop}px`);
    this.results.toggleAttribute(
      'data-sterling-toolbar-stuck',
      Math.abs(toolbar.getBoundingClientRect().top - rootTop - toolbarTop) <= 2
    );
  }

  disconnectedCallback() {
    if (this.updateFrame !== null) cancelAnimationFrame(this.updateFrame);
    this.updateFrame = null;
    this.scrollTarget?.removeEventListener('scroll', this.scheduleUpdate);
    this.scrollTarget = null;
    this.resizeObserver?.disconnect();
    this.abortController?.abort();
    this.results?.removeAttribute('data-sterling-toolbar-stuck');
  }
}

class SterlingCollectionStory extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.frame = this.querySelector('[data-sterling-collection-frame]');
    this.rail = this.querySelector('[data-sterling-collection-rail]');
    this.items = [...this.querySelectorAll('[data-sterling-collection-item]')];
    this.dots = [...this.querySelectorAll('[data-sterling-collection-dot]')];
    this.scrollTarget = null;
    this.updateFrame = null;
    this.measureFrame = null;
    this.railFrame = null;
    this.programmaticFrame = null;
    this.anchor = null;
    this.settingRailScroll = false;

    this.scheduleUpdate = () => {
      if (!this.active || this.updateFrame !== null) return;
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    };

    this.scheduleMeasure = () => {
      if (this.measureFrame !== null) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        this.measure();
      });
    };

    this.scheduleRailUpdate = () => {
      if (this.railFrame !== null) return;
      this.railFrame = requestAnimationFrame(() => {
        this.railFrame = null;
        this.updateDots();
        if (!this.active || this.settingRailScroll || !Number.isFinite(this.start)) return;
        this.anchor = { progress: this.getProgress(), position: this.rail.scrollLeft };
      });
    };

    this.refresh = () => {
      const shouldActivate = Boolean(
        this.frame && this.rail && this.items.length > 1 && mobileQuery.matches && !reducedMotionQuery.matches
      );
      if (!shouldActivate) {
        this.deactivate();
        this.updateDots();
        return;
      }
      this.active = true;
      this.dataset.scrollStory = 'active';
      this.bindScrollTarget();
      this.scheduleMeasure();
    };

    this.rail?.addEventListener('scroll', this.scheduleRailUpdate, { passive: true, signal });
    window.addEventListener('resize', this.scheduleMeasure, { signal });
    window.visualViewport?.addEventListener('resize', this.scheduleMeasure, { signal });
    mobileQuery.addEventListener('change', this.refresh, { signal });
    reducedMotionQuery.addEventListener('change', this.refresh, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.refresh, { signal });
    this.refresh();
  }

  bindScrollTarget() {
    const target = getScrollEventTarget();
    if (target === this.scrollTarget) return;
    this.scrollTarget?.removeEventListener('scroll', this.scheduleUpdate);
    this.scrollTarget = target;
    this.scrollTarget.addEventListener('scroll', this.scheduleUpdate, { passive: true });
  }

  measure() {
    this.style.removeProperty('--sterling-collection-story-height');
    this.overflow = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
    this.updateDots();
    if (!this.active || this.overflow < 1) {
      if (this.overflow < 1) this.deactivate();
      return;
    }

    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    this.holdDistance = Math.min(120, Math.max(72, viewportHeight * .11));
    this.distance = this.overflow + this.holdDistance;
    this.travelDistance = this.overflow;
    this.anchor = null;
    this.style.setProperty('--sterling-collection-story-height', `${this.scrollHeight + this.distance}px`);

    const scrollContainer = getScrollContainer();
    this.rootTop = scrollContainer === document.scrollingElement ? 0 : scrollContainer.getBoundingClientRect().top;
    this.stickyTop = Number.parseFloat(getComputedStyle(this.frame).top) || 88;
    this.start = getScrollTop() + this.frame.getBoundingClientRect().top - this.rootTop - this.stickyTop;
    this.update();
  }

  getProgress() {
    return Math.min(this.distance || 0, Math.max(0, getScrollTop() - this.start));
  }

  getPosition(progress) {
    if (!this.anchor) return Math.min(this.overflow, progress);
    const anchorProgress = Math.min(this.travelDistance, this.anchor.progress);
    const anchorPosition = Math.min(this.overflow, Math.max(0, this.anchor.position));
    if (progress <= anchorProgress) return anchorProgress < 1 ? anchorPosition : anchorPosition * (progress / anchorProgress);
    if (progress >= this.travelDistance) return this.overflow;
    const remaining = this.travelDistance - anchorProgress;
    return anchorPosition + (this.overflow - anchorPosition) * ((progress - anchorProgress) / remaining);
  }

  update() {
    if (!this.active || !Number.isFinite(this.start)) return;
    const frameTop = this.frame.getBoundingClientRect().top - this.rootTop;
    if (frameTop > this.stickyTop + 1) {
      this.start = getScrollTop() + frameTop - this.stickyTop;
      if (this.anchor) this.anchor.progress = 0;
    }
    this.settingRailScroll = true;
    this.rail.scrollLeft = this.getPosition(this.getProgress());
    this.updateDots();
    if (this.programmaticFrame !== null) cancelAnimationFrame(this.programmaticFrame);
    this.programmaticFrame = requestAnimationFrame(() => {
      this.programmaticFrame = null;
      this.settingRailScroll = false;
    });
  }

  updateDots() {
    if (!this.rail || !this.items.length || !this.dots.length) return;
    const firstOffset = this.items[0].offsetLeft;
    let activeIndex = 0;
    let distance = Infinity;
    this.items.forEach((item, index) => {
      const nextDistance = Math.abs(item.offsetLeft - firstOffset - this.rail.scrollLeft);
      if (nextDistance < distance) {
        distance = nextDistance;
        activeIndex = index;
      }
    });
    this.dots.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
  }

  deactivate() {
    this.active = false;
    this.anchor = null;
    this.scrollTarget?.removeEventListener('scroll', this.scheduleUpdate);
    this.scrollTarget = null;
    this.removeAttribute('data-scroll-story');
    this.style.removeProperty('--sterling-collection-story-height');
    [this.updateFrame, this.measureFrame, this.programmaticFrame].forEach((frame) => {
      if (frame !== null) cancelAnimationFrame(frame);
    });
    this.updateFrame = null;
    this.measureFrame = null;
    this.programmaticFrame = null;
    this.settingRailScroll = false;
  }

  disconnectedCallback() {
    this.deactivate();
    if (this.railFrame !== null) cancelAnimationFrame(this.railFrame);
    this.railFrame = null;
    this.abortController?.abort();
  }
}

if (!customElements.get('sterling-collection-story')) {
  customElements.define('sterling-collection-story', SterlingCollectionStory);
}

if (!customElements.get('sterling-collection-sticky-toolbar')) {
  customElements.define('sterling-collection-sticky-toolbar', SterlingCollectionStickyToolbar);
}
